/* eslint-disable max-depth */
/* eslint-disable max-lines */
import { useEffect, useRef, useState, forwardRef, useMemo, useCallback, useImperativeHandle } from 'react';
import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import { ModelViewer, ModelViewerRef, Snapshot } from '@fuxi/model-viewer-react';
import { useDeepCompareEffect, useKeyPress, useSize } from 'ahooks';
import { EditorMode } from '@fuxi/react-img-editor';
import { theme, Modal, Spin, ConfigProvider } from '@fuxi/eevee-ui';
import { isNil } from 'lodash-es';
import { VideoPlayer, FileType as VideoPlayerType } from '@fuxi/video-player';
import {
  ExportFileType,
  TaskMode,
  FrameRateRecord,
  TotalFramesRecord,
  ExportVersionStatus,
  ExportVersionFile,
  TaskPermissionType,
} from 'common/types';
import { sendEvent } from 'common/tracker';
import { CustomEvent } from 'common/tracker/custom-event';
import { eventEmitter } from '@/utils/eventEmitter';
import { PlayerRef, PlayerSource } from '@/types/playControl';
import { CommentAssetPosition, CommentSourceType, AspectType } from '@/types';
import { useTaskEditorContext } from '@/context/TaskEditorContext';
import { getTransform } from '@/components/MediaPlayer/util';
import MediaPlayer from '@/components/MediaPlayer';
import CommentToolLight from '@/components/CommentToolLight';
import ZoomToolbar from '@/components/ZoomToolbar';
import { useAppDispatch, useAppSelector, useRestoreCommentAndPlayer } from '@/hooks';
import { useTaskDetailContext } from '@/context/TaskDetailContext';
import ErrorPreviewImage from '@/components/ErrorPreviewImage';
import { useCommentContext } from '@/context/CommentContext';
import { selectUserInfo } from '@/store/user';
import {
  selectGroupedComments,
  selectNeedsRebuildAnnotation,
  setCommentDrawerType,
  setIsCommentDrawerOpen,
  setNeedsRebuildAnnotation,
} from '@/store/comment';
import CommentTool from '../CommentTool';
import ModelViewerControl from '../ModelViewerControl';
import TurnTable from '../TurnTable';
import PlayList from '../PlayList';
import { FrameRateEmitterProps } from './PlayRateDropdown';

import {
  computeImageTransformInContainer,
  computeImageAspectMode,
  groupReviewByTakeIndex,
  groupReviewByFrameIndex,
} from './util';
import PlayerBar, { PlayerBarActionMode } from './PlayerBar';
import cx from './index.module.css';
import { compareVersion } from '@/utils/version';

dayjs.extend(duration);

export enum PlayerViewerEvent {
  FileChange = 'FILE_CHANGE', // 切换文件事件
  SaveAnnotation = 'SAVE_ANNOTATION', // 保存注释反馈
  ClearAnnotation = 'CLEAR_ANNOTATION',
  iframeRate = 'IFRAME_RATE',
}
interface PlayViewControlOriginalProps {
  // height?: number;
  // url: string;
  // transform: VideoTransform;
  // changePlayerTransform: (params: VideoTransform) => void;
  // disabled: boolean;
  playerWidth: number;
  playerHeight: number;
  playerSource?: PlayerSource;
  originFrameRate: Array<FrameRateRecord>;
  defaultFrameRate?: number;
  defaultFrameIndex?: number;
  defaultTakeIndex?: number;
  totalFrames: Array<TotalFramesRecord>;
  // onCameraChange?: () => void;
  crossOrigin?: 'anonymous' | 'use-credentials';
  canComment?: boolean;
  playList?: Array<ExportVersionFile>;
}
export type PlayViewerControlRef = PlayerRef &
  ModelViewerRef & {
    getCurrentTakeIndex: () => number;
    getCurrentFrameIndex: () => number;
    getCurrentFrameRate: () => number;
    getContainer: () => HTMLElement;
  };

type PlayViewControlProps = PlayViewControlOriginalProps;

export const optionalDefaultProps = {
  onPlay: () => {},
  onPause: () => {},
  onSeek: () => {},
  crossOrigin: 'use-credentials',
};

export const defaultProps = {
  ...optionalDefaultProps,
};

export const scaleDelta = 0.1;
export const BASE_SCALE_RATIO = 1;

export const WHEEL_MAX_SCALE_RATIO = 1;

const PlayViewerControl = forwardRef<PlayViewerControlRef, PlayViewControlProps>(
  (
    {
      originFrameRate,
      defaultFrameRate,
      totalFrames: originTotalFrames,
      defaultTakeIndex,
      defaultFrameIndex,
      playerWidth,
      playerHeight,
      crossOrigin,
      canComment,
      // playList,
    },
    ref,
  ) => {
    const dispatch = useAppDispatch();
    const { token } = theme.useToken();
    const needsRebuildAnnotation = useAppSelector(selectNeedsRebuildAnnotation);
    const groupedComments = useAppSelector(selectGroupedComments);
    const {
      mode,
      exportFile,
      setCurrentIndex,
      setExportFile,
      previewType,
      snapShotRef,
      playerTransform,
      setPreviewType,
      setFileLoading,
      setPlayerTransform,
      projectConfig,
      workflow,
      editorMode,
      onModelViewerSnapShot,
      commentToolRef,
      rebuildCachedComment,
      clearCachedComment,
      curVersion,
      getCachedComment,
      modelViewerHide,
      saveDefaultComment,
      fileIdByCurrentIndex,
      setCurrentFileLoaded,
      currentFileLoaded,
      videoStyleRef,
      aspectMode,
      setAspectMode,
      isCommentToolActive,
      setIsCommentToolActive,
      fileLoading,
      currentIndex,
      currentFile,
      fileList,
      isPlayList,
      setIsPlayList,
      currentPlayIndex,
      setCurrentPlayIndex,
      onPlayIndexChange,
      setPlayList: setPlayListAction,
      isPreviewError,
      setIsPreviewError,
    } = useTaskEditorContext();
    const isPlayListModeRef = useRef<boolean>(isPlayList);
    const { roleInfo, projectCode, stage, requirementType, isTaskOver } = useTaskDetailContext();
    const { restoreComment } = useRestoreCommentAndPlayer();
    const { permission } = roleInfo || {};
    const userInfo = useAppSelector(selectUserInfo);
    const { curComment, filteredCommentList, curCommentId, isEditing, setIsEditing, setIsPreviewing, setCurCommentId } =
      useCommentContext();
    const [isPause, setIsPause] = useState(true);

    const [dataSources, setDataSources] = useState<any>([]);
    const [frameIndex, setFrameIndex] = useState<any>(defaultFrameIndex);
    const [originalFrameRate, setOriginalFrameRate] = useState<number>(
      originFrameRate?.length > 0 ? originFrameRate[0].frames : 24,
    );
    const frameIndexRef = useRef<number>(defaultFrameIndex ?? 0);

    const [curTakeIndex, setCurTakeIndex] = useState(defaultTakeIndex || 0);
    const [frameRate, setFrameRate] = useState<number>(
      defaultFrameRate || (originFrameRate?.length > 0 ? originFrameRate[0].frames : 24),
    );
    const [isPlayerPause, setIsPlayerPause] = useState(true);
    const [loading, setLoading] = useState(true); // 是否正在加载
    const [playList, setPlayList] = useState<ExportVersionFile[]>([]);
    // const [currentPlayIndex, setCurrentPlayIndex] = useState(0);

    const [cachedVideoTrans, setCachedVideoTrans] = useState({ transX: 0, transY: 0 });
    const [cachedVideoScale, setCachedVideoScale] = useState(1);
    const [videoStyle, setVideoStyle] = useState({
      width: '100%',
      height: '100%',
      transform: 'translate(0, 0) scale(1)',
    });

    const [hasCachedComment, setHasCachedComment] = useState(false);

    const playerWrapperRef = useRef<any>();
    const isMoving = useRef<boolean>(false);
    const isSpaceKeyActive = useRef<boolean>(false);
    // const curSnapShot = useRef<Snapshot | undefined>();

    const playerRef = useRef<any>();
    const containerRef = useRef<any>();
    const containerSize = useSize(containerRef);
    const toolbarWrapperRef = useRef<any>();

    const [isLoop, setIsLoop] = useState(true);

    const [modelThumbMode, setModelThumbMode] = useState(false);
    const [modelInspectShow, setModelInspectShow] = useState(false);

    const [isDrawDisabled, setIsDrawDisabled] = useState(false);
    const [framesPerPx, setFramesPerPx] = useState(0); // 每个像素对应的帧数
    const [currentReviewList, setCurrentReviewList] = useState<CommentAssetPosition[]>([]);
    const isSavingRef = useRef(false);

    const xRef = useRef(0),
      yRef = useRef(0);
    const eventsListener = useRef({
      mousewheel: (e: any) => {},
      mousedown: (e: any) => {},
      mousemove: (e: any) => {},
      mouseup: (e: any) => {},
    });
    const totalFrames = useMemo(() => {
      if (isPlayList) {
        let t = 0;
        for (let i = 0; i < playList.length; i++) {
          const item = playList[i];
          const totalFrames = item.rules.find((rule) => rule.ruleType === 'TotalFrames')?.checkResult as number;
          const fps = item.rules.find((rule) => rule.ruleType === 'FrameRate')?.checkResult as number;
          t += totalFrames;
        }
        return t;
      } else if (originTotalFrames?.length > 0) {
        return originTotalFrames[curTakeIndex]?.totalFrames || 0;
      } else {
        return 0;
      }
    }, [curTakeIndex, originTotalFrames, playList, isPlayList]);
    const rebuild = () => {
      setHasCachedComment(false);
      //进入编辑状态
      rebuildCachedComment();
    };
    const clear = () => {
      setHasCachedComment(false);
      clearCachedComment();
    };
    useEffect(() => {
      setCurTakeIndex(defaultTakeIndex || 0);
    }, [defaultTakeIndex]);

    const is030DCCVersion = useCallback(() => {
      //判断curVersion?.qualityInspection?.dccVersion大于等于0.3.0版本
      return compareVersion(curVersion?.qualityInspection?.dccVersion ?? '0.0.0', '0.3.0');
    }, [curVersion]);

    useEffect(() => {
      const indexFileKey = 'uuid';

      if (groupedComments) {
        if (isPlayList) {
          let reviews: CommentAssetPosition[] = [];
          let offset = 0;
          playList?.forEach((item, index) => {
            reviews = reviews.concat(
              groupedComments?.[item[indexFileKey]]?.map((item) => {
                return {
                  ...item.assetPosition,
                  frameIndex: Math.round(offset + item.assetPosition.frameIndex),
                  uniqueId: item.uniqueId,
                  createTime: item.createTime,
                };
              }) ?? [],
            );
            offset += item.rules.find((rule) => rule.ruleType === 'TotalFrames')?.checkResult as number;
          });

          //reviews按照frameIndex排序
          reviews.sort((a, b) => a.frameIndex - b.frameIndex);
          setCurrentReviewList(reviews);
        } else {
          let list = groupedComments?.[fileList[currentIndex]?.[indexFileKey]];

          const reviews: CommentAssetPosition[] =
            list?.map((item) => {
              return {
                ...item.assetPosition,
                frameIndex: Math.round(item.assetPosition.frameIndex),
                uniqueId: item.uniqueId,
                createTime: item.createTime,
              };
            }) ?? [];
          //reviews按照frameIndex排序
          reviews.sort((a, b) => a.frameIndex - b.frameIndex);
          setCurrentReviewList(reviews);
        }
      }
    }, [groupedComments, currentIndex, fileList, curVersion, playList, isPlayList]);

    useEffect(() => {
      // if (isCommentToolActive) {
      if (curVersion?.status !== ExportVersionStatus.Pass && getCachedComment()) {
        setHasCachedComment(true);
      } else {
        clearCachedComment();
      }
      // }
    }, [curVersion?.status]);

    const onFrameChange = useCallback(
      (index: number, offset: number = 0) => {
        const newFrameIndex = previewType === ExportFileType.Animation ? Math.floor(index) + 1 : index;

        setFrameIndex((prevIndex) => {
          if (prevIndex !== newFrameIndex) {
            return newFrameIndex;
          }
          return prevIndex;
        });
        frameIndexRef.current = newFrameIndex - offset;
      },
      [previewType, totalFrames, isLoop],
    );
    const changeVideoStyle = ({ scale, transX, transY, width, height }) => {
      //if width or height is 0, set width or height 100%
      if (width === 0 || height === 0) {
        setVideoStyle({
          width: '100%',
          height: '100%',
          transform: `translate3d(${transX}px, ${transY}px, 0px) scale3d(${scale}, ${scale}, 1)`,
        });
      } else {
        setVideoStyle({
          width: `${width}px`,
          height: `${height}px`,
          transform: `translate3d(${transX}px, ${transY}px, 0px) scale3d(${scale}, ${scale}, 1)`,
        });
      }
    };
    const changePlayerTransform = ({ scale, transX, transY, width, height }) => {
      // console.log('%c changePlayerTransform', 'color: #ff0000', scale, transX, transY, width, height);
      if (playerRef.current) {
        setPlayerTransform({
          ...playerTransform,
          scale,
          transX,
          transY,
          width,
          height,
        });
        videoStyleRef.current = {
          ...playerTransform,
          scale,
          transX,
          transY,
          width,
          height,
        };
        changeVideoStyle({ scale, transX, transY, width, height });
      }
    };

    const onVideoOrAnimationLoaded = (playerSource?: PlayerSource[]) => {
      console.log('%c [onVideoOrAnimationLoaded]', 'color: yellow; background: black', playerSource);
      if (playerSource?.length) {
        setDataSources(playerSource);
        let takeName = playerSource[0].name;
        if (!isNil(curCommentId)) {
          const comment = filteredCommentList.find((com) => com.uniqueId === curCommentId);
          if (comment) {
            const { assetPosition } = comment;
            const { takeIndex, frameIndex } = assetPosition;
            takeName = playerSource[takeIndex].name;
          }
        }
        const currentTakeIndex = findTakeIndexByName(takeName);
        if (currentTakeIndex >= 0) {
          setCurTakeIndex(currentTakeIndex);
          setOriginalFrameRate(originFrameRate[currentTakeIndex]?.frames || 30);
          setFrameRate(originFrameRate[currentTakeIndex]?.frames || 30);
        }

        // feat: frameRate change when origin toogle.
        const frameRateProps: FrameRateEmitterProps = { frameRate, originalFrameRate };
        // console.log('frameRateProps', frameRateProps);
        eventEmitter.emit(PlayerViewerEvent.iframeRate, frameRateProps);
      }

      onLoad();
    };
    const onVideoOrAnimationError = () => {
      setFileLoading(false);
    };

    const groupedReviewList = useMemo(() => {
      let reviewMap = {},
        reviewKeys: string[] = [];

      if (currentReviewList) {
        const grouped = groupReviewByTakeIndex(currentReviewList);
        // 再按照每个takeIndex里的frameIndex分组

        if (!isNil(curTakeIndex)) {
          const curTakeReviewList = grouped[curTakeIndex];
          if (curTakeReviewList) {
            reviewMap = groupReviewByFrameIndex(curTakeReviewList);
            reviewKeys = Object.keys(reviewMap);
          }
        }
      }
      return { reviewMap, reviewKeys };
    }, [currentReviewList, curTakeIndex]);

    const reset = () => {
      //重置画板

      commentToolRef.current?.clearAnnotation();
      setFrameIndex(0);
      frameIndexRef.current = 0;
      // 连播切换不能暂停状态
      console.log('reset', isPlayListModeRef.current);
      if (!isPlayListModeRef.current) {
        setIsPause(true);
      }

      setDataSources([]);
      setCurTakeIndex(0);
      setOriginalFrameRate(originFrameRate && originFrameRate?.length > 0 ? originFrameRate[0].frames : 24);
      // changePlayerTransform({ scale: 1, transX: 0, transY: 0, width: 0, height: 0 });
      setCurrentFileLoaded(false);
    };
    const findTakeIndexByName = useCallback(
      (name: string) => {
        let index = -1;
        if (originFrameRate) {
          originFrameRate.forEach((item: FrameRateRecord, index1: number) => {
            if (item.name === name) {
              index = index1;
            }
          });
        }

        return index;
      },
      [originFrameRate],
    );
    const onLoad = (playerSource?: PlayerSource[]) => {
      setFileLoading(false);
      setCurrentFileLoaded(true);

      setLoading(false);

      setIsCommentToolActive(true);

      sendEvent(CustomEvent.CheckModel, {
        et: 'CLK',
        c1: curVersion?.uniqueId,
        c2: curVersion?.taskId,
        c3: curVersion?.parentTaskId,
        c4: curVersion?.projectId,
        c5: currentFile?.fileName,
      });
    };

    const onModelViewerLoaded = () => {
      setFileLoading(false);

      // 看上去没啥用，在上面已经设置了
      // const { offsetWidth, offsetHeight } = playerWrapperRef.current;
      // console.log('onModelViewerLoaded', offsetWidth, offsetHeight);
      // setPlayerTransform({ width: offsetWidth, height: offsetHeight, transX: 0, transY: 0, scale: 1 });
      onLoad();
    };
    const onError = () => {
      setFileLoading(false);
      setCurrentFileLoaded(true);
      setLoading(false);
      onVideoOrAnimationError();
      setIsPreviewError(true);
      setIsCommentToolActive(true);
    };

    // 增强 ref 的能力，添加方法控制播放状态
    useImperativeHandle(ref, () => ({
      ...playerRef.current,

      getCurrentFrameIndex: () => {
        return Math.round(frameIndexRef.current);
      },
      getCurrentTakeIndex: () => curTakeIndex,
      getCurrentFrameRate: () => frameRate,
      play: handlePlay,
      pause: handlePause,
      getContainer: () => containerRef.current,
      //切换动画轨道
      changeTakeIndex: (index: number) => {
        setCurTakeIndex(index);
      },
      //切换帧
      changeFrameIndex: (index: number) => {
        onFrameChange(index);
      },
    }));

    const handlePlay = useCallback(async () => {
      if (commentToolRef.current) {
        if (isEditing && curComment) {
          // await commentToolRef.current?.updateAnnotation({
          //   comment: curComment,
          // });
          // setIsEditing(false);
        } else {
          if (!isSavingRef.current) {
            isSavingRef.current = true;
            await saveDefaultComment();
            isSavingRef.current = false;
          }
        }
        setCurCommentId(-1);
        setIsPreviewing(false);
      }

      setIsPause(false);
      dispatch(setNeedsRebuildAnnotation(false));
      console.log('handlePlay', dataSources, curTakeIndex, isLoop, playerRef?.current);
      if (isPlayList) {
        playerRef?.current?.play(isLoop);
      } else if (dataSources && dataSources.length > 0 && curTakeIndex >= 0) {
        playerRef?.current.startRender && playerRef?.current.startRender();
        playerRef?.current?.play(isLoop, dataSources[curTakeIndex].name);
      }
    }, [isEditing, curComment, dataSources, curTakeIndex, isLoop, playerRef?.current]);

    const handlePause = useCallback(() => {
      setIsPause(true);
      playerRef?.current?.pause?.();
      dispatch(setNeedsRebuildAnnotation(false));
    }, [dataSources, curTakeIndex, isPause, setIsPause]);

    const handleSeek = useCallback(
      async (frameIndex, mode: PlayerBarActionMode) => {
        if (commentToolRef.current) {
          if (isEditing && curComment) {
            handlePause();
            return;
          } else {
            if (!isSavingRef.current) {
              isSavingRef.current = true;
              await saveDefaultComment();
              isSavingRef.current = false;
            }
          }

          if (
            previewType === ExportFileType.Video &&
            (mode === PlayerBarActionMode.Down || mode === PlayerBarActionMode.ShortCutReview)
          ) {
            showPickedComment(frameIndex, mode === PlayerBarActionMode.ShortCutReview ? 0 : framesPerPx);
            return;
          }
        }
        // setCurCommentId(-1);
        // dispatch(setCurrentCommentIndex(-1));
        setIsPreviewing(false);
      },
      [curComment, isEditing, curTakeIndex, playerRef?.current, previewType, groupedReviewList, framesPerPx],
    );
    const handleCommentPreview = (review: CommentAssetPosition) => {
      if (curCommentId === review.uniqueId) {
        return;
      }
      // find review in groupedComments
      const comment = filteredCommentList.find((item) => item.uniqueId === review.uniqueId);
      if (comment) {
        restoreComment(comment);

        setIsPreviewing(true);
        sendEvent(CustomEvent.CheckReview, {
          et: 'CLK',
          c1: projectCode,
          c2: roleInfo?.originRole,
          c3: requirementType,
          c4: stage,
        });
      }
    };
    const showPickedComment = (frameIndex, framesPerPx = 0) => {
      //framesPerPx为0时，不做左右偏差
      const index = frameIndex;

      for (let i = 0; i < groupedReviewList.reviewKeys.length; i++) {
        //在 frameIndex 位置找到对应的注释反馈，如果左右偏差curTickWidth也能找到
        if (Math.abs(parseFloat(groupedReviewList.reviewKeys[i]) - index) <= framesPerPx) {
          handleCommentPreview(groupedReviewList.reviewMap[groupedReviewList.reviewKeys[i]][0]);
          return;
        }
      }
      setCurCommentId(-1);
      setIsPreviewing(false);
    };

    const onCameraChange = useCallback(async () => {
      if (playerRef.current?.startRender) {
        playerRef.current?.startRender();
      }
      setIsPlayerPause(false);
      if (!isSavingRef.current) {
        //新增
        isSavingRef.current = true;
        await saveDefaultComment();
        isSavingRef.current = false;
      }

      setIsPreviewing(false);
      dispatch(setNeedsRebuildAnnotation(false));
      //当前不选中反馈
      setCurCommentId(-1);
      setIsPreviewing(false);
    }, []);

    const changeTakeIndex = async (index) => {
      if (playerRef.current?.startRender) {
        playerRef.current?.startRender();
      }
      setIsPlayerPause(false);
      if (!isSavingRef.current) {
        isSavingRef.current = true;
        //新增
        await saveDefaultComment();
        isSavingRef.current = false;
      }

      setCurTakeIndex(index);
      setIsPreviewing(false);
      dispatch(setNeedsRebuildAnnotation(false));
    };
    useDeepCompareEffect(() => {
      // if (isPlayList) {
      //   return;
      // }
      // 容器变化，重新计算videoStyle
      if ((previewType === ExportFileType.Video || previewType === ExportFileType.Image) && containerSize) {
        const { width: containerWidth, height: containerHeight } = containerSize;
        let mediaShowWidth = playerWidth;
        let mediaShowHeight = playerHeight;

        // 默认原始尺寸
        let scale = playerTransform.scale;
        const computedMode = computeImageAspectMode({
          imageWidth: mediaShowWidth,
          imageHeight: mediaShowHeight,
          containerWidth,
          containerHeight,
        });
        setAspectMode(computedMode);
        if (computedMode === AspectType.aspectFit) {
          const transform = computeImageTransformInContainer(
            {
              imageWidth: mediaShowWidth,
              imageHeight: mediaShowHeight,
              containerWidth,
              containerHeight,
            },
            Number.MAX_VALUE,
          );

          scale = transform.scale;
        }
        const parentCenter = { x: containerWidth / 2, y: containerHeight / 2 };
        const center = { x: mediaShowWidth / 2, y: mediaShowHeight / 2 };

        const cached = {
          transX: parentCenter.x - center.x,
          transY: parentCenter.y - center.y,
        };
        setCachedVideoTrans(cached);
        setCachedVideoScale(scale);
        changePlayerTransform({
          ...playerTransform,
          transX: cached.transX,
          transY: cached.transY,
          scale,
          width: mediaShowWidth,
          height: mediaShowHeight,
        });
      } else if (containerSize) {
        changePlayerTransform({
          ...playerTransform,
          width: containerSize.width,
          height: containerSize.height,
          scale: 1,
          transX: 0,
          transY: 0,
        });
      }
    }, [containerSize, playerHeight, playerWidth, previewType, exportFile, isPlayList]);

    const onAspectChange = (type) => {
      let newTransform;
      if (type === AspectType.aspectFill) {
        newTransform = {
          ...videoStyleRef.current,
          ...cachedVideoTrans,
          scale: 1,
        };
      } else {
        if (!containerSize) return;
        const { width: containerWidth, height: containerHeight } = containerSize;
        const mediaShowWidth = playerWidth;
        const mediaShowHeight = playerHeight;

        const { scale } = computeImageTransformInContainer(
          {
            imageWidth: mediaShowWidth,
            imageHeight: mediaShowHeight,
            containerWidth,
            containerHeight,
          },
          Number.MAX_VALUE,
        );
        newTransform = {
          ...videoStyleRef.current,
          ...cachedVideoTrans,
          scale: scale,
        };
      }
      changePlayerTransform(newTransform);
      setAspectMode(type);
    };
    const dispatchZoomChange = (ratio: number, centerX?: number, centerY?: number) => {
      const player = playerWrapperRef.current;
      const parent = containerRef.current;
      const { offsetWidth, offsetHeight, offsetLeft, offsetTop } = player;

      let transf = getTransform(player);

      const { scale } = videoStyleRef.current;
      let newScale = scale * ratio;
      let newRatio = ratio;
      const { scale: originScale } = computeImageTransformInContainer({
        imageWidth: player.offsetWidth,
        imageHeight: player.offsetHeight,
        containerWidth: parent.offsetWidth,
        containerHeight: parent.offsetHeight,
      });

      if (newScale <= parseFloat(originScale.toFixed(4))) {
        newScale = originScale;
        newRatio = newScale / scale;
      } else if (newScale > parseFloat((originScale * 10).toFixed(2))) {
        newScale = originScale * 10;
        newRatio = parseFloat((originScale * 10).toFixed(2)) / scale;
      }

      const mergedCenterX = centerX ?? parent.offsetWidth / 2;
      const mergedCenterY = centerY ?? parent.offsetHeight / 2;
      const diffRatio = newRatio - 1;
      /** Deviation calculated from image size */
      const diffImgX = diffRatio * offsetWidth * 0.5;
      const diffImgY = diffRatio * offsetHeight * 0.5;
      /** The difference between the click position and the edge of the document */
      const diffOffsetLeft = diffRatio * (mergedCenterX - transf.transX - offsetLeft);
      const diffOffsetTop = diffRatio * (mergedCenterY - transf.transY - offsetTop);
      /** Final positioning */
      let newX = transf.transX - (diffOffsetLeft - diffImgX);
      let newY = transf.transY - (diffOffsetTop - diffImgY);
      if (ratio < 1 && newScale === originScale) {
        const parentCenter = { x: parent.offsetWidth / 2, y: parent.offsetHeight / 2 };
        const center = { x: offsetWidth / 2, y: offsetHeight / 2 };

        newX = parentCenter.x - center.x;
        newY = parentCenter.y - center.y;
      }
      videoStyleRef.current = { ...videoStyleRef.current, scale: newScale, transX: newX, transY: newY };
      changePlayerTransform(videoStyleRef.current);
    };
    const onZoom = (direction: number) => {
      const ratio = direction > 0 ? BASE_SCALE_RATIO + scaleDelta : BASE_SCALE_RATIO / (BASE_SCALE_RATIO + scaleDelta);
      dispatchZoomChange(ratio);
      // 如果是aspectFit模式，缩放后需要切换为aspectFill模式
      if (aspectMode === AspectType.aspectFit) {
        setAspectMode(AspectType.aspectFill);
      }
    };

    const onMouseWheel = (e: any) => {
      e.preventDefault();
      e.stopPropagation();
      const { clientX, clientY } = e;
      const video = playerWrapperRef.current;
      const parent = containerRef.current;
      if (!video || !parent) {
        return;
      }
      const scaleRatio = Math.abs(e.deltaY / 100);
      // Limit the maximum scale ratio
      const mergedScaleRatio = Math.min(scaleRatio, WHEEL_MAX_SCALE_RATIO);
      // Scale the ratio each time
      let ratio = BASE_SCALE_RATIO + mergedScaleRatio * scaleDelta;
      if (e.deltaY > 0) {
        ratio = BASE_SCALE_RATIO / ratio;
      }
      dispatchZoomChange(ratio, clientX, clientY);
    };

    const onMouseDown = (e) => {
      e.preventDefault();
      if (isSpaceKeyActive.current) {
        isMoving.current = true;

        let transf = getTransform(playerWrapperRef.current);
        xRef.current = e.clientX - transf.transX; // 图片初始位置
        yRef.current = e.clientY - transf.transY; // 图片初始位置
      }
    };
    const onMouseMove = (e) => {
      e.preventDefault();

      if (isSpaceKeyActive.current && isMoving.current && playerWrapperRef.current && containerRef.current) {
        const video = playerWrapperRef.current;

        const parent = containerRef.current;
        let { scale } = getTransform(video);

        let moveX = e.clientX - xRef.current;
        let moveY = e.clientY - yRef.current;

        const leftPixel = 100;

        let maxVideoCenter = {
          x: (video.offsetWidth * scale) / 2 + parent.offsetWidth - leftPixel,
          y: (video.offsetHeight * scale) / 2 + parent.offsetHeight - leftPixel,
        };
        let maxTransX = maxVideoCenter.x - video.offsetWidth / 2;
        let maxTransY = maxVideoCenter.y - video.offsetHeight / 2;

        let minVideoCenter = {
          x: leftPixel - (video.offsetWidth * scale) / 2,
          y: leftPixel - (video.offsetHeight * scale) / 2,
        };
        let minTransX = minVideoCenter.x - video.offsetWidth / 2;
        let minTransY = minVideoCenter.y - video.offsetHeight / 2;

        if (moveX > maxTransX) {
          moveX = maxTransX;
        } else if (moveX < minTransX) {
          moveX = minTransX;
        }
        if (moveY > maxTransY) {
          moveY = maxTransY;
        } else if (moveY < minTransY) {
          moveY = minTransY;
        }

        videoStyleRef.current = { ...videoStyleRef.current, transX: moveX, transY: moveY };

        changePlayerTransform(videoStyleRef.current);
      }
    };
    useEffect(() => {
      if (mode === TaskMode.Edit) {
        onModelThumbModeChange(true);
      } else {
        onModelThumbModeChange(false);
      }
    }, [mode]);

    const onMouseUp = (e) => {
      e.preventDefault();
      if (isSpaceKeyActive.current) {
        isMoving.current = false;
        xRef.current = 0;
        yRef.current = 0;
      }
    };
    const onModelThumbModeChange = (enable: boolean) => {
      setModelThumbMode(enable);
      setModelInspectShow(!enable);
    };
    const bindEvents = () => {
      eventsListener.current = {
        mousewheel: onMouseWheel.bind(this),
        mousedown: onMouseDown.bind(this),
        mousemove: onMouseMove.bind(this),
        mouseup: onMouseUp.bind(this),
      };
      containerRef.current?.addEventListener('mousewheel', eventsListener.current.mousewheel);
      playerWrapperRef.current?.addEventListener('mousedown', eventsListener.current.mousedown);

      containerRef.current?.addEventListener('mousemove', eventsListener.current.mousemove);
      // 图形移出父盒子取消移动事件,防止移动过快触发鼠标移出事件,导致鼠标弹起事件失效
      containerRef.current?.addEventListener('mouseleave', eventsListener.current.mouseup);
      // 鼠标弹起后停止移动
      containerRef.current?.addEventListener('mouseup', eventsListener.current.mouseup);
    };
    const unbindEvents = (keepMouseWheel = false) => {
      !keepMouseWheel && containerRef?.current?.removeEventListener('mousewheel', eventsListener.current.mousewheel);
      playerWrapperRef?.current?.removeEventListener('mousedown', eventsListener.current.mousedown);
      containerRef?.current?.removeEventListener('mousemove', eventsListener.current.mousemove);
      containerRef.current?.removeEventListener('mouseleave', eventsListener.current.mouseup);
      containerRef.current?.removeEventListener('mouseup', eventsListener.current.mouseup);
      eventsListener.current = {
        mousewheel: (e: any) => {},
        mousedown: (e: any) => {},
        mousemove: (e: any) => {},
        mouseup: (e: any) => {},
      };
    };
    const handleSpaceDown = (event) => {
      event.preventDefault();
      if (event.type === 'keydown') {
        isSpaceKeyActive.current = true;
        playerWrapperRef.current.style.cursor = 'grab';
        setIsDrawDisabled(true);
      } else {
        isSpaceKeyActive.current = false;
        playerWrapperRef.current.style.cursor = 'default';
        setIsDrawDisabled(false);
      }
    };

    useKeyPress('space', handleSpaceDown, { events: ['keydown', 'keyup'], target: window });

    useEffect(() => {
      if (!isNil(exportFile)) {
        if (previewType === ExportFileType.Animation) {
          //如果正在查看注释反馈,需要重新开始渲染
          if (playerRef.current?.startRender) {
            playerRef.current?.startRender();
          }
        }
        if (previewType === ExportFileType.Video || previewType === ExportFileType.Image) {
          bindEvents();
        }
        return () => {
          unbindEvents();
          reset();
        };
      }
    }, [exportFile, previewType]);

    useEffect(() => {
      const rate = defaultFrameRate || (originFrameRate?.length > 0 ? originFrameRate[0].frames : 24);
      setFrameRate(rate);
      setOriginalFrameRate(originFrameRate && originFrameRate?.length > 0 ? originFrameRate[0].frames : 24);
    }, [originFrameRate, defaultFrameRate]);

    const onModelViewerAction = (action: string, payload: any) => {
      sendEvent(action, {
        et: 'CLK',
        c1: projectCode,
        c2: roleInfo?.originRole,
        c3: requirementType,
        c4: stage,
        c5: JSON.stringify(payload),
      });
    };

    const onTurntableChanged = async () => {
      if (!isSavingRef.current) {
        isSavingRef.current = true;
        await saveDefaultComment();
        isSavingRef.current = false;
      }

      setIsPreviewing(false);
      dispatch(setNeedsRebuildAnnotation(false));
      //当前不选中反馈
      setCurCommentId(-1);
      setIsPreviewing(false);
    };

    useEffect(() => {
      if (needsRebuildAnnotation && !isNil(curCommentId) && currentFileLoaded) {
        const comment = filteredCommentList.find((item) => item.uniqueId === curCommentId);
        if (comment) {
          const isRestorePlayStatus =
            comment.type === CommentSourceType.Animation || comment.type === CommentSourceType.Video;
          if (isRestorePlayStatus) {
            //截取预览内容快照
            if (playerRef && playerRef.current) {
              setCurTakeIndex(comment.assetPosition.takeIndex);

              if (isPlayList && playList?.length > 0) {
                let offset = 0;
                playList?.forEach((item, index) => {
                  if (item.uuid === comment.assetPosition.fileId) {
                    setCurrentPlayIndex(index);
                    playerRef.current?.changeCurrentPlayIndex(index);
                    setFrameIndex(comment.assetPosition.frameIndex + offset);
                    frameIndexRef.current = comment.assetPosition.frameIndex;
                  }
                  offset += item.rules.find((rule) => rule.ruleType === 'TotalFrames')?.checkResult as number;
                });
              } else {
                setFrameIndex(comment.assetPosition.frameIndex);
                frameIndexRef.current = comment.assetPosition.frameIndex;
              }

              setIsPause(true);
              playerRef.current.pause && playerRef.current.pause();

              playerRef.current.restoreSnapshot(comment.assetPosition.snapShot);
            }
            //还原绘制内容
            if (commentToolRef?.current) {
              commentToolRef.current.restoreAnnotation(comment, false);
            }
          } else {
            // 清空画板
            if (commentToolRef?.current) {
              commentToolRef.current.clearAnnotation();
            }

            if (comment.type === CommentSourceType.CheckResult || comment.type === CommentSourceType.Normal) {
              dispatch(setCommentDrawerType(comment.type));
              dispatch(setIsCommentDrawerOpen(true));
            }
          }
        } else {
          // 清空画板
          if (commentToolRef?.current) {
            commentToolRef.current.clearAnnotation();
          }
        }
      }
    }, [needsRebuildAnnotation, curCommentId, currentFileLoaded, filteredCommentList, isPlayList, playList]);

    const hasReviewPermission = useMemo(() => {
      return permission?.includes(`${projectCode}.${TaskPermissionType.TaskReview}`);
    }, [projectCode, permission]);

    const getIsEventDisabled = useMemo(() => {
      return (
        !hasReviewPermission &&
        curCommentId > -1 &&
        filteredCommentList[curCommentId]?.creator?.uniqueId !== userInfo?.uniqueId
      );
    }, [hasReviewPermission, curCommentId, filteredCommentList, userInfo?.uniqueId]);

    const onPlayIndexClick = (index: number) => {
      playerRef.current?.changeCurrentPlayIndex(index, 0);
      setIsPreviewError(false);
    };
    // 播放列表改变
    const onPlayListChange = (list: ExportVersionFile[]) => {
      onPlayIndexChange();
      setCurrentPlayIndex(0);
      setPlayList(list);
      setPlayListAction(list);
      setTimeout(() => {
        playerRef.current?.changeCurrentPlayIndex(0);
        setIsPreviewError(false);
        playerRef.current?.pause?.();
        playerRef.current?.goToFrame(0);
      }, 10);
    };

    // 切换播放列表联动文件列表
    // useEffect(() => {
    //   if (playList.length ?? currentPlayIndex) {
    //     const index = fileList.findIndex(item => item.id === playList[currentPlayIndex].id);
    //     setCurrentIndex(index);
    //     setExportFile(playList[currentPlayIndex].exportFileUrl);
    //     console.log('setCurrentIndex', index);
    //   }
    //   // setCurrentPlayIndex(index);
    // }, [currentPlayIndex, playList]);

    // // 切换文件列表联动播放列表
    // useEffect(() => {
    //   if (isPlayList && playListRef.current) {
    //     onPlayListChange(playListRef.current);
    //   }
    //   onPlayIndexChange();

    //   playerRef.current?.pause?.();
    //   playerRef.current?.goToFrame?.(0);

    //   isPlayListModeRef.current = isPlayList;
    // }, [isPlayList]);

    const onTogglePlayList = (enable) => {
      setIsPlayList(enable);
      if (enable && playListRef.current) {
        onPlayListChange(playListRef.current);
      }
      playerRef.current?.pause?.();
      playerRef.current?.goToFrame?.(0);
      onPlayIndexChange();

      isPlayListModeRef.current = enable;
    };

    const playListRef = useRef<ExportVersionFile[]>();

    useEffect(() => {
      playListRef.current = playList;
    }, [playList]);

    // useEffect(() => {
    //   // 不能判断isPlayList，只联动currentIndex
    //   if (!playListRef.current || !currentFile) return;
    //   if (playListRef.current?.length > 0 && fileList?.length > 0) {
    //     const index = playListRef.current.findIndex(item => item.uuid === currentFile.uuid);
    //     if (index > -1) {
    //       setCurrentPlayIndex(index);
    //       playerRef.current?.changeCurrentPlayIndex(index);
    //       setIsPreviewError(false);
    //     } else {
    //       playerRef.current?.pause?.();
    //       setIsPlayList(false);
    //       setExportFile(currentFile.exportFileUrl);
    //     }
    //   }
    // }, [currentFile, fileList]);

    return (
      <ConfigProvider
        theme={{
          components: {
            Tooltip: {
              colorBgSpotlight: token.colorBgElevated,
            },
          },
        }}>
        <>
          <Spin spinning={fileLoading} size="large" tip="加载中...">
            {/** 反馈工具 */}
            {canComment &&
              !isTaskOver &&
              // isCommentToolActive &&
              editorMode === EditorMode.ANNOTATION && (
                <div
                  style={{
                    height: hasReviewPermission && !isTaskOver ? '58px' : '0',
                    width: '100%',
                    display: !isPreviewError ? 'block' : 'none',
                    position: 'relative',
                    background: token.colorBgContainer,
                    borderRight: `1px solid ${token.colorBorderSecondary}`,
                  }}
                  ref={toolbarWrapperRef}></div>
              )}
            <div
              onClick={() => {
                // 点击画板，取消其他元素的聚焦态
                document.activeElement?.id.includes('w-e-textarea-') && document.activeElement?.blur?.();
              }}
              id="player-container"
              style={{
                width: '100%',
                height: !isPreviewError && hasReviewPermission && !isTaskOver ? 'calc(100% - 58px)' : '100%',
                position: 'relative',
                overflow: 'clip',
              }}
              ref={containerRef}>
              {/** 展示画板 */}
              <div style={videoStyle} ref={playerWrapperRef} className={cx('player-wrapper')}>
                {curVersion && isPreviewError && (
                  <ErrorPreviewImage
                    ref={playerRef}
                    onLoad={onError}
                    dccStatus={curVersion?.dccStatus}
                    downloadUrl={curVersion?.downloadUrl}
                    errorMsgs={fileList?.[currentIndex]?.errorMsgs}
                  />
                )}

                {(previewType === ExportFileType.Video || previewType === ExportFileType.Image) && (
                  <MediaPlayer
                    type={previewType}
                    fps={frameRate}
                    onLoad={onVideoOrAnimationLoaded}
                    onFrameChange={onFrameChange}
                    ref={playerRef}
                    url={exportFile as string}
                    totalFrames={totalFrames}
                    crossOrigin={crossOrigin}
                    onError={onError}
                    playList={playList}
                    isPlayListMode={isPlayList}
                    currentPlayIndex={currentPlayIndex}
                    onPause={setIsPause}
                    onCurrentPlayIndexChange={setCurrentPlayIndex}
                  />
                )}
                {previewType === ExportFileType.Babylon && !modelViewerHide && (
                  <div
                    className={cx('model-viewer-outer', {
                      'float-window': mode === TaskMode.Edit && previewType === ExportFileType.Babylon,
                      small: modelThumbMode,
                    })}>
                    <div className={cx('model-viewer-wrap')}>
                      {/* 批注模式显示小窗相关操作 */}
                      <ModelViewerControl
                        mode={mode}
                        modelThumbMode={modelThumbMode}
                        setModelThumbMode={onModelThumbModeChange}
                        modelInspectShow={modelInspectShow}
                        setModelInspectShow={setModelInspectShow}
                        takeSnapShot={onModelViewerSnapShot}
                      />
                      <ModelViewer
                        snapshot={
                          !fileIdByCurrentIndex
                            ? (snapShotRef.current[fileIdByCurrentIndex as string] as Snapshot)
                            : undefined
                        }
                        ref={playerRef}
                        showHUD={!modelThumbMode}
                        showPanel={modelInspectShow}
                        assetPath={exportFile as string}
                        onLoad={onModelViewerLoaded}
                        onError={onError}
                        workflow={workflow}
                        scale={projectConfig.scale}
                        refModels={projectConfig.refModels}
                        outline={projectConfig.outline}
                        onAction={onModelViewerAction}
                        onCameraChange={onCameraChange}
                      />
                    </div>
                  </div>
                )}
                {previewType === ExportFileType.Animation && (
                  <ModelViewer
                    snapshot={
                      !fileIdByCurrentIndex
                        ? (snapShotRef.current[fileIdByCurrentIndex as string] as Snapshot)
                        : undefined
                    }
                    workflow={'MRA' as any}
                    showHUD={!modelThumbMode}
                    showPanel={modelInspectShow}
                    ref={playerRef}
                    assetPath={exportFile as string}
                    onLoad={onVideoOrAnimationLoaded}
                    onError={onError}
                    onFrameChange={onFrameChange}
                    onCameraChange={onCameraChange}
                    animations={originTotalFrames}
                  />
                )}

                {/* Turn table预览**/}
                {(previewType === ExportFileType.TurnTable || previewType === ExportFileType.LibraryTurntable) && (
                  <TurnTable
                    ref={playerRef}
                    assetPaths={exportFile as string[]}
                    onChange={onTurntableChanged}
                    showArrows={previewType === ExportFileType.LibraryTurntable}
                    onLoad={onLoad}
                    onError={onError}
                    invert={false}
                  />
                )}
                {/* Turntable 视频预览**/}
                {previewType === ExportFileType.TurntableVideo && (
                  <VideoPlayer
                    width="100%"
                    height="100%"
                    turntableRow={2}
                    turntableFrame={16}
                    onError={onError}
                    crossOrigin="use-credentials"
                    onLoad={() => onLoad()}
                    progressive={true}
                    type={VideoPlayerType.TurntableVideo}
                    url={exportFile?.[0] as string}
                    thumbVideoUrl={exportFile?.[1] as string}
                    // url={
                    //   'https://mmvp-fos.apps-hp.danlu.netease.com/api/sdk/0/task_attachment$$$0$$$586451e14b8042bd8914447a4fb65c56/all-ori.mp4'
                    // }
                    // thumbVideoUrl="https://mmvp-fos.apps-hp.danlu.netease.com/api/sdk/0/task_attachment$$$None$$$1717578049141/lwq7trvf_747803.mp4"
                  />
                )}
                {/* 文件预览 **/}
                {/* {previewType === ExportFileType.Other && <StaticPreview onLoad={onLoad} onError={onError} />} */}
                {/* <RebuildComment playerRef={playerRef as any} showToolbar={false} parentRef={containerRef} />
          {<Rebuild3dAnnotation parentRef={containerRef} />} */}
                {!isTaskOver && canComment && editorMode === EditorMode.ANNOTATION && (
                  <CommentToolLight
                    toolbarWrapper={toolbarWrapperRef}
                    parentRef={playerWrapperRef}
                    frameIndex={frameIndex}
                    takeIndex={curTakeIndex}
                    getCurrentFrameRate={() => frameRate}
                    getCurrentFrameIndex={() => Math.round(frameIndexRef.current)}
                    getCurrentTakeName={() => dataSources[curTakeIndex]?.name ?? ''}
                    getCurrentTakeIndex={() => curTakeIndex}
                    playerTransform={playerTransform}
                    isEventDisabled={getIsEventDisabled}
                    isDrawDisabled={isDrawDisabled}
                    showToolbar={hasReviewPermission}
                    ref={commentToolRef as any}
                    enabled={isCommentToolActive && !isPreviewError}
                  />
                )}
                {!isTaskOver && isCommentToolActive && editorMode === EditorMode.PALETTE && <CommentTool />}
              </div>
              {(previewType === ExportFileType.Video || previewType === ExportFileType.Image) && (
                <ZoomToolbar
                  onAspectChange={onAspectChange}
                  aspectMode={aspectMode}
                  onZoom={onZoom}
                  {...playerTransform}
                />
              )}
            </div>
          </Spin>
          {(previewType === ExportFileType.Video || previewType === ExportFileType.Animation) && (
            <PlayerBar
              isEventDisabled={isMoving.current && isSpaceKeyActive.current}
              playerRef={playerRef}
              type={previewType}
              dataSources={dataSources}
              playList={playList}
              onTogglePlayList={onTogglePlayList}
              onPlay={handlePlay}
              onPause={handlePause}
              onSeek={handleSeek}
              defaultTakeIndex={curTakeIndex}
              totalFrames={totalFrames}
              reviewList={currentReviewList}
              isPause={isPause}
              setIsPause={(bool) => setIsPause(bool)}
              isLoop={isLoop}
              setIsLoop={(loop) => {
                setIsLoop(loop);
              }}
              loading={loading}
              setLoading={setLoading}
              frameIndex={frameIndex}
              curTakeIndex={curTakeIndex}
              setCurTakeIndex={changeTakeIndex}
              isKeyEventActive={mode !== TaskMode.Edit}
              frameRate={frameRate}
              setFrameRate={setFrameRate}
              originalFrameRate={originalFrameRate}
              setFileLoading={setFileLoading}
              onFramesPerPxChange={(width) => setFramesPerPx(width)}
            />
          )}
          <PlayList
            editModalVisible={false}
            onChange={onPlayListChange}
            currentPlayIndex={currentPlayIndex}
            onIndexChange={onPlayIndexClick}></PlayList>

          <Modal
            title="反馈异常中断"
            open={hasCachedComment}
            onOk={rebuild}
            onCancel={clear}
            okText="编辑"
            cancelText="放弃">
            <p>识别到您有一个未保存的反馈，是否继续编辑？</p>
          </Modal>
        </>
      </ConfigProvider>
    );
  },
);

export default PlayViewerControl;
